import { useId, useState } from 'react';
import { Button, DatePicker, Segmented, Tooltip } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { OnboardingCohortRange, OnboardingPeriodPreset } from '@mygate/common';
import { getOnboardingCustomRange, getOnboardingPresetRange } from '@/utils/onboardingPeriod';

export function OnboardingPeriodPicker({
  range,
  anchor,
  onChange,
  label = 'Onboarding-Zeitraum',
  disabled = false,
}: {
  range: OnboardingCohortRange;
  anchor: string;
  onChange: (range: OnboardingCohortRange) => void;
  label?: string;
  disabled?: boolean;
}) {
  const [custom, setCustom] = useState(false);
  const [from, setFrom] = useState<string | null>(range.from.slice(0, 10));
  const [to, setTo] = useState<string | null>(new Date(new Date(range.to).getTime() - 1).toISOString().slice(0, 10));
  const fromId = useId();
  const toId = useId();
  const selected = !custom
    ? ((['DAY', 'WEEK', 'MONTH'] as const).find(preset => {
        const candidate = getOnboardingPresetRange(preset, anchor);
        return candidate.from === range.from && candidate.to === range.to;
      }) ?? 'CUSTOM')
    : 'CUSTOM';
  const draft = from && to ? getOnboardingCustomRange(from, to, anchor) : null;
  const invalidOrder = from && to && from > to;
  const lastCalendarDay = anchor.slice(0, 10);
  const exceedsCutoff = (from && from > lastCalendarDay) || (to && to > lastCalendarDay);

  return (
    <div className="onboarding-period-filter">
      <Segmented<OnboardingPeriodPreset>
        disabled={disabled}
        aria-label={label}
        value={selected}
        options={[
          { label: <Tooltip title="Letzte 24 Stunden bis zum gewählten Stichtag">1 Tag</Tooltip>, value: 'DAY' },
          { label: <Tooltip title="Letzte 7 Tage bis zum gewählten Stichtag">1 Woche</Tooltip>, value: 'WEEK' },
          { label: <Tooltip title="Letzte 30 Tage bis zum gewählten Stichtag">1 Monat</Tooltip>, value: 'MONTH' },
          { label: 'Eigener Zeitraum', value: 'CUSTOM' },
        ]}
        onChange={preset => {
          setCustom(preset === 'CUSTOM');
          if (preset !== 'CUSTOM') {
            const next = getOnboardingPresetRange(preset, anchor);
            setFrom(next.from.slice(0, 10));
            setTo(next.to.slice(0, 10));
            onChange(next);
          }
        }}
      />
      {selected === 'CUSTOM' && (
        <div className="onboarding-custom-period">
          <div className="onboarding-date-field">
            <label htmlFor={fromId}>Von</label>
            <DatePicker
              disabled={disabled}
              id={fromId}
              aria-label="Zeitraum von"
              value={from ? dayjs(from) : null}
              format="DD.MM.YYYY"
              placeholder="Startdatum"
              maxDate={dayjs(lastCalendarDay)}
              onChange={value => setFrom(value?.format('YYYY-MM-DD') ?? null)}
              status={invalidOrder ? 'error' : undefined}
              showNow={false}
            />
          </div>
          <div className="onboarding-date-field">
            <label htmlFor={toId}>Bis einschließlich</label>
            <DatePicker
              disabled={disabled}
              id={toId}
              aria-label="Zeitraum bis"
              value={to ? dayjs(to) : null}
              format="DD.MM.YYYY"
              placeholder="Enddatum"
              maxDate={dayjs(lastCalendarDay)}
              onChange={value => setTo(value?.format('YYYY-MM-DD') ?? null)}
              status={invalidOrder ? 'error' : undefined}
              showNow={false}
            />
          </div>
          <Button
            type="primary"
            icon={<CalendarOutlined />}
            disabled={disabled || !draft}
            onClick={() => draft && onChange(draft)}
          >
            Anwenden
          </Button>
          {invalidOrder && (
            <span className="onboarding-period-error" role="alert">
              Das Startdatum muss vor oder am Enddatum liegen.
            </span>
          )}
          {from && to && !invalidOrder && !draft && (
            <span className="onboarding-period-error" role="alert">
              {exceedsCutoff
                ? 'Das Datum liegt nach dem Datenstand (UTC).'
                : 'Bitte einen gültigen Zeitraum mit höchstens 366 Tagen auswählen.'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
